<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import Icon from 'next/icon/Icon.vue';
import OnboardingStepLayout from './OnboardingStepLayout.vue';
import { useRegisterOnboardingNav } from '../useOnboardingNav';

defineProps({
  venueName: { type: String, default: '' },
});

const store = useStore();
const { t } = useI18n();

const templates = computed(() => store.state.onboarding.procedureTemplates);
const selectedIds = computed(() => store.state.onboarding.procedureTemplateIds);
const assistantName = computed(
  () =>
    store.state.onboarding.assistantName ||
    store.getters['onboarding/suggestedAssistantName']
);
const uiFlags = computed(() => store.state.onboarding.uiFlags);
const error = computed(() => store.state.onboarding.error);

function templateTitle(template) {
  const key = `ASSISTANT_MGMT.PROCEDURES.STANDARD.TEMPLATES.${template.id}`;
  const translated = t(key);
  return translated === key ? template.name : translated;
}

function isSelected(template) {
  return selectedIds.value.includes(template.id);
}

function toggleTemplate(template) {
  if (template.available === false) return;
  store.dispatch('onboarding/toggleProcedureTemplateId', template.id);
}

const continueSetup = () => store.dispatch('onboarding/saveCapabilities');

const primaryLabel = computed(() => {
  if (uiFlags.value.isSaving || uiFlags.value.isProvisioning) {
    return t('ONBOARDING_WIZARD.CAPABILITIES.CTA_LOADING');
  }
  return t('ONBOARDING_WIZARD.CAPABILITIES.CTA');
});

const skipAssistant = () => store.dispatch('onboarding/skipAssistant');

useRegisterOnboardingNav(() => ({
  secondary: {
    label: t('ONBOARDING_WIZARD.SKIP'),
    loading: uiFlags.value.isProvisioning,
    onClick: skipAssistant,
  },
  primary: {
    label: primaryLabel.value,
    loading: uiFlags.value.isSaving || uiFlags.value.isProvisioning,
    onClick: continueSetup,
  },
}));
</script>

<template>
  <OnboardingStepLayout
    :step-label="t('ONBOARDING_WIZARD.OVERVIEW.PHASES.starting_with_ai.TITLE')"
    :title="
      t('ONBOARDING_WIZARD.CAPABILITIES.TITLE', { name: assistantName })
    "
    :description="t('ONBOARDING_WIZARD.CAPABILITIES.DESCRIPTION')"
    wide
  >
    <template #header-extra>
      <p class="m-0 text-sm text-slate-500 dark:text-slate-400">
        {{ t('ONBOARDING_WIZARD.CAPABILITIES.PROVISION_HINT') }}
      </p>
    </template>

    <div class="flex flex-col gap-4 w-full">
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full">
        <button
          v-for="template in templates"
          :key="template.id"
          type="button"
          class="rounded-xl border p-5 text-left transition-all min-h-[8rem] disabled:opacity-50"
          :class="
            isSelected(template)
              ? 'border-woot-500 bg-woot-50/60 dark:bg-woot-900/20 ring-1 ring-woot-500'
              : 'border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900/40 hover:border-woot-300 shadow-sm'
          "
          :disabled="template.available === false"
          @click="toggleTemplate(template)"
        >
          <div class="flex items-start justify-between gap-3 mb-3">
            <Icon
              icon="i-lucide-sparkles"
              class="size-6 text-woot-500 shrink-0"
            />
            <Icon
              v-if="isSelected(template)"
              icon="i-lucide-check-circle-2"
              class="size-6 text-woot-500 shrink-0"
            />
          </div>
          <p class="font-semibold text-slate-900 dark:text-white m-0 text-base">
            {{ templateTitle(template) }}
          </p>
          <p class="text-sm text-slate-500 dark:text-slate-400 m-0 mt-1.5">
            {{ template.description }}
          </p>
        </button>
      </div>
      <p v-if="error" class="text-sm text-red-600 dark:text-red-400 m-0">
        {{ error }}
      </p>
    </div>
  </OnboardingStepLayout>
</template>
